'use client';

import { useGetEsportOdds } from '@/api/mightykiwi/mightyKiwi';
import type { PandaScoreMatch } from '@/api/teams/teams.types';
import { getGameFromVideogameSlug } from '@/helpers/videogame.helpers';
import { findOddsForMatch } from '@/helpers/odds.helpers';

type MatchOddsProps = {
  match: PandaScoreMatch;
};

const formatOdds = (value?: number | null): string =>
  value && value > 0 ? value.toFixed(2) : '—';

const OddPill = ({ label, value }: { label: string; value?: number | null }) => (
  <div className="flex flex-1 flex-col items-center gap-1 rounded-md bg-component-bg px-3 py-2">
    <span className="w-full truncate text-center text-gray text-xs">{label}</span>
    <span className="font-semibold text-light-blue text-base tabular-nums">
      {formatOdds(value)}
    </span>
  </div>
);

export const MatchOdds = ({ match }: MatchOddsProps) => {
  const game = getGameFromVideogameSlug(match.videogame?.slug) ?? undefined;
  const query = useGetEsportOdds(game ? { game } : undefined);

  if (query.isLoading) {
    return <div className="h-24 animate-pulse rounded-lg bg-card-bg" />;
  }

  const odds = query.data?.data
    ? findOddsForMatch(match, query.data.data)
    : null;

  if (!odds) {
    return (
      <p className="rounded-lg bg-card-bg p-4 text-blue-gray text-sm">
        Odds aren’t available for this match right now.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-card-bg p-4">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm text-white">
          {odds.name ?? 'Match Winner'}
        </span>
        {odds.tournament && (
          <span className="truncate text-gray text-xs">{odds.tournament}</span>
        )}
      </div>

      <div className="flex items-stretch gap-2">
        <OddPill label={odds.team1 ?? 'Team 1'} value={odds.team1Odds} />
        {odds.drawOdds != null && <OddPill label="Draw" value={odds.drawOdds} />}
        <OddPill label={odds.team2 ?? 'Team 2'} value={odds.team2Odds} />
      </div>

      {odds.matchUrl && (
        <a
          className="self-start text-light-blue text-xs hover:underline"
          href={odds.matchUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          View bookmaker odds
        </a>
      )}

      <p className="text-[10px] text-gray">
        Odds provided by our partners and matched by team &amp; start time.
      </p>
    </div>
  );
};

export default MatchOdds;
